import { useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { normalizeSong } from '../../../utils/songUtils';
import logger from '../../../utils/logger';

const countReplies = (comments = []) =>
  comments.reduce((n, c) => n + (c.replies?.length || 0), 0);

/** True when `next` has more top-level comments or replies than `prev`. */
const hasMoreCommentActivity = (prevComments = [], nextComments = []) => {
  if (nextComments.length > prevComments.length) return true;
  return countReplies(nextComments) > countReplies(prevComments);
};

/**
 * Live Firestore subscription for the open song document so comments, replies,
 * likes, and other embedded fields stay in sync across tabs/devices.
 */
export function useSongDetailsListener(songId, setSongDetails, { onError } = {}) {
  useEffect(() => {
    if (!songId) return undefined;

    const songRef = doc(db, 'songs', songId);
    logger.debug('[SongPage] Subscribing to song updates:', songId);

    const unsubscribe = onSnapshot(
      songRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          logger.warn('[SongPage] Song document missing:', songId);
          return;
        }

        const next = normalizeSong({ id: snapshot.id, ...snapshot.data() });
        const fromCache = snapshot.metadata.fromCache;

        setSongDetails((prev) => {
          if (!prev || prev.id !== next.id) return next;

          // Server-confirmed snapshots always win.
          if (!fromCache) return next;

          const prevComments = prev.comments || [];
          const nextComments = next.comments || [];

          // Cached snapshot with new comments/replies (e.g. offline sync) — apply.
          if (hasMoreCommentActivity(prevComments, nextComments)) return next;

          // Same or fewer comments in cache — keep local comments (optimistic rows)
          // but still merge song-level fields like likes and emoji reactions.
          return { ...next, comments: prevComments };
        });
      },
      (error) => {
        logger.error('[SongPage] Song listener error:', error);
        onError?.(error);
      }
    );

    return () => {
      logger.debug('[SongPage] Unsubscribing from song updates:', songId);
      unsubscribe();
    };
  }, [songId, setSongDetails, onError]);
}
